import { DEFAULT_STATUSES } from '../../FlowchartView/TaskNode/constants';
import { SopAPI } from '../../../../utils/api';
import { dispatchDocVariablesUpdated } from '../../../../utils/docVariablesEvents';
import {
  clearVariableRowValues,
  countFilledVariableSlots,
} from '../../../../utils/variableScope';
import {
  collectAttachedDocIds,
  collectDescendantSprintIds,
  getActiveTasks,
  resetTaskProgress,
  resetTasksProgressForStatuses,
  summarizeResetImpact,
} from '../../utils/resetCampaignProgress';

const DOC_FETCH_CONCURRENCY = 4;

async function mapWithConcurrency(items, limit, fn) {
  if (!items.length) return;
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      await fn(items[index]);
    }
  });
  await Promise.all(workers);
}

export async function fetchDocsById(docIds) {
  const docsById = new Map();
  await mapWithConcurrency(docIds || [], DOC_FETCH_CONCURRENCY, async (docId) => {
    try {
      const doc = await SopAPI.get(docId);
      if (doc?.id) docsById.set(doc.id, doc);
    } catch {
      // Missing/binned docs are skipped; mission status reset still proceeds.
    }
  });
  return docsById;
}

export async function resolveTreeSprints(rootId, allSprints, loadFullSprint) {
  const descendantIds = collectDescendantSprintIds(rootId, allSprints);
  const byId = new Map();
  for (const id of descendantIds) {
    if (id === rootId) continue;
    const existing = allSprints.find((s) => s.id === id);
    if (existing && Array.isArray(existing.tasks)) {
      byId.set(id, existing);
      continue;
    }
    if (typeof loadFullSprint === 'function') {
      try {
        const loaded = await loadFullSprint(id);
        if (loaded) byId.set(id, loaded);
        else if (existing) byId.set(id, existing);
      } catch {
        if (existing) byId.set(id, existing);
      }
    } else if (existing) {
      byId.set(id, existing);
    }
  }
  return { descendantIds, byId };
}

/** Reset keeps names/colors/locks; wipes unlocked values only. */
export function clearVariableValues(variables) {
  if (!Array.isArray(variables) || variables.length === 0) return variables;
  return variables.map((v) => (v?.locked ? v : clearVariableRowValues(v)));
}

/** How many unlocked non-empty values will be cleared (sprints + optional docs). */
export function countFilledVariableValues(sprints = [], docsById = null) {
  let count = 0;
  const countVars = (vars) => {
    for (const v of vars || []) {
      if (!v || v.locked) continue;
      count += countFilledVariableSlots(v);
    }
  };
  for (const sp of sprints) countVars(sp?.variables);
  if (docsById) {
    const docs = docsById instanceof Map ? docsById.values() : Object.values(docsById);
    for (const doc of docs) countVars(doc?.variables);
  }
  return count;
}

function cloneVariableRows(variables) {
  if (!Array.isArray(variables)) return variables;
  return variables.map((v) => ({
    ...v,
    values: Array.isArray(v.values) ? [...v.values] : v.values,
  }));
}

function variableValuesChanged(prev, next) {
  if (prev === next) return false;
  if (!Array.isArray(prev) || !Array.isArray(next) || prev.length !== next.length) return true;
  return next.some((v, i) => {
    const older = prev[i];
    if (String(v.value ?? '') !== String(older?.value ?? '')) return true;
    const a = Array.isArray(v.values) ? v.values : null;
    const b = Array.isArray(older?.values) ? older.values : null;
    if (!a && !b) return false;
    if (!a || !b || a.length !== b.length) return true;
    return a.some((slot, j) => String(slot ?? '') !== String(b[j] ?? ''));
  });
}

function iterateDocs(docsById) {
  if (!docsById) return [];
  return docsById instanceof Map ? [...docsById.entries()] : Object.entries(docsById);
}

/** Snapshot `{ id, variables }` for every loaded doc that has rows (undo). */
export function snapshotDocVariables(docsById) {
  const out = [];
  for (const [id, doc] of iterateDocs(docsById)) {
    if (!Array.isArray(doc?.variables) || doc.variables.length === 0) continue;
    out.push({ id, variables: cloneVariableRows(doc.variables) });
  }
  return out;
}

/** Unlocked rows emptied; locked rows kept. Only docs that actually change. */
export function buildClearedDocVariableUpdates(docsById) {
  const out = [];
  for (const [id, doc] of iterateDocs(docsById)) {
    const vars = doc?.variables;
    if (!Array.isArray(vars) || vars.length === 0) continue;
    const next = clearVariableValues(vars);
    if (!variableValuesChanged(vars, next)) continue;
    out.push({ id, variables: next });
  }
  return out;
}

/** Persist variable rows on docs and refresh any open editor showing them. */
export async function persistDocVariables(updates) {
  if (!Array.isArray(updates) || updates.length === 0) return;
  const saved = [];
  await Promise.all(
    updates.map(async ({ id, variables }) => {
      try {
        await SopAPI.update(id, { variables });
        saved.push({ id, variables });
      } catch {
        // Doc save failed — campaign reset still proceeds.
      }
    })
  );
  dispatchDocVariablesUpdated(saved);
}

export function runMultiReset({ setLocalSprint, allSprints, docsById }) {
  setLocalSprint((prev) => {
    if (!prev?.tasks) return prev;
    const nextTasks = prev.tasks.map((task) => {
      const cid = task.sourceCampaign?.id;
      const campaign = cid ? allSprints.find((s) => s.id === cid) : null;
      const st = campaign?.statuses || DEFAULT_STATUSES;
      return resetTaskProgress(task, st, { docsById });
    });
    return { ...prev, tasks: nextTasks };
  });
}

export function runSingleReset({
  localSprintId,
  setLocalSprint,
  statuses,
  allSprints,
  onUpdateSprint,
  docsById,
  tree,
  clearedDocUpdates = null,
}) {
  if (!localSprintId) return;
  const descendantIds = tree?.descendantIds || collectDescendantSprintIds(localSprintId, allSprints);
  let rootNext = null;
  setLocalSprint((prev) => {
    if (!prev) return prev;
    const st = prev.statuses || statuses;
    rootNext = {
      ...prev,
      tasks: resetTasksProgressForStatuses(prev.tasks, st, { docsById }),
      variables: clearVariableValues(prev.variables),
    };
    return rootNext;
  });
  if (rootNext && onUpdateSprint) {
    onUpdateSprint(rootNext, { immediate: true });
  }
  if (onUpdateSprint) {
    for (const id of descendantIds) {
      if (id === localSprintId) continue;
      const sp = tree?.byId?.get(id) || allSprints.find((s) => s.id === id);
      if (!sp) continue;
      onUpdateSprint(
        {
          ...sp,
          tasks: resetTasksProgressForStatuses(sp.tasks, sp.statuses || DEFAULT_STATUSES, {
            docsById,
          }),
          variables: clearVariableValues(sp.variables),
        },
        { immediate: true }
      );
    }
  }
  // Empty unlocked values on every attached doc in this campaign tree.
  void persistDocVariables(
    clearedDocUpdates || buildClearedDocVariableUpdates(docsById)
  );
}

export function openMultiCampaignReset({
  localSprint,
  setLocalSprint,
  allSprints,
  modals,
  recordAction,
  resetDocsByIdRef,
}) {
  const rootTasks = getActiveTasks(localSprint);
  const doReset = () =>
    runMultiReset({
      setLocalSprint,
      allSprints,
      docsById: resetDocsByIdRef.current,
    });

  modals.handleOpenResetCampaignModal({
    isMulti: true,
    rootMissionCount: rootTasks.length,
    nestedMissionCount: 0,
    onConfirm: () => {
      const oldVirtual = (localSprint?.tasks || []).map((t) => ({ ...t }));
      doReset();
      recordAction?.({
        type: 'RESET_CAMPAIGN_PROGRESS',
        description: 'Reset campaign progress (combined view)',
        undo: () =>
          setLocalSprint((prev) =>
            prev ? { ...prev, tasks: oldVirtual.map((t) => ({ ...t })) } : prev,
          ),
        redo: () => doReset(),
      });
    },
  });

  (async () => {
    const docIds = collectAttachedDocIds(rootTasks);
    const docsById = await fetchDocsById(docIds);
    resetDocsByIdRef.current = docsById;
    const impact = summarizeResetImpact({ rootTasks, nestedTasks: [], docsById });
    modals.handleUpdateResetCampaignModal({ loading: false, ...impact });
  })();
}

export function openSingleCampaignReset({
  localSprint,
  setLocalSprint,
  statuses,
  allSprints,
  loadFullSprint,
  onUpdateSprint,
  modals,
  recordAction,
  resetDocsByIdRef,
  resetTreeSprintsRef,
}) {
  if (!localSprint?.id) return;

  const sprintId = localSprint.id;
  const rootTasksSync = getActiveTasks(localSprint);
  const preliminaryIds = collectDescendantSprintIds(sprintId, allSprints);
  let nestedMissionCountSync = 0;
  const preliminarySprints = [localSprint];
  for (const id of preliminaryIds) {
    if (id === sprintId) continue;
    const sp = allSprints.find((s) => s.id === id);
    nestedMissionCountSync += getActiveTasks(sp).length;
    if (sp) preliminarySprints.push(sp);
  }
  const variableValueCountSync = countFilledVariableValues(preliminarySprints);

  const doReset = (clearedDocUpdates) =>
    runSingleReset({
      localSprintId: sprintId,
      setLocalSprint,
      statuses,
      allSprints,
      onUpdateSprint,
      docsById: resetDocsByIdRef.current,
      tree: resetTreeSprintsRef.current,
      clearedDocUpdates,
    });

  modals.handleOpenResetCampaignModal({
    isMulti: false,
    rootMissionCount: rootTasksSync.length,
    nestedMissionCount: nestedMissionCountSync,
    variableValueCount: variableValueCountSync,
    onConfirm: () => {
      const oldRoot = (localSprint.tasks || []).map((t) => ({ ...t }));
      const oldRootVariables = cloneVariableRows(localSprint.variables);
      const tree = resetTreeSprintsRef.current;
      const descendantIds = tree?.descendantIds || collectDescendantSprintIds(sprintId, allSprints);
      const nestedOld = [];
      for (const id of descendantIds) {
        if (id === sprintId) continue;
        const sp = tree?.byId?.get(id) || allSprints.find((s) => s.id === id);
        if (sp) {
          nestedOld.push({
            id,
            sprint: sp,
            tasks: (sp.tasks || []).map((t) => ({ ...t })),
          });
        }
      }
      const docsById = resetDocsByIdRef.current;
      const oldDocVariables = snapshotDocVariables(docsById);
      const clearedDocUpdates = buildClearedDocVariableUpdates(docsById);

      doReset(clearedDocUpdates);

      recordAction?.({
        type: 'RESET_CAMPAIGN_PROGRESS',
        description: 'Reset campaign progress',
        undo: () => {
          const restoredRoot = {
            ...localSprint,
            tasks: oldRoot.map((t) => ({ ...t })),
            variables: oldRootVariables,
          };
          setLocalSprint((prev) => (prev ? { ...prev, ...restoredRoot } : prev));
          onUpdateSprint?.(restoredRoot, { immediate: true });
          if (onUpdateSprint) {
            // `sprint` is the pre-reset snapshot, so spreading it restores
            // the old variable values alongside the old tasks.
            nestedOld.forEach(({ sprint, tasks: nestedTasks }) => {
              onUpdateSprint(
                { ...sprint, tasks: nestedTasks.map((t) => ({ ...t })) },
                { immediate: true }
              );
            });
          }
          void persistDocVariables(oldDocVariables);
        },
        redo: () => doReset(clearedDocUpdates),
      });
    },
  });

  (async () => {
    const tree = await resolveTreeSprints(sprintId, allSprints, loadFullSprint);
    resetTreeSprintsRef.current = tree;

    const rootTasks = getActiveTasks(localSprint);
    const nestedTasks = [];
    for (const sp of tree.byId.values()) {
      nestedTasks.push(...getActiveTasks(sp));
    }

    const treeSprints = [localSprint, ...tree.byId.values()];
    const docIds = collectAttachedDocIds([...rootTasks, ...nestedTasks]);
    const docsById = await fetchDocsById(docIds);
    resetDocsByIdRef.current = docsById;
    const variableValueCount = countFilledVariableValues(treeSprints, docsById);

    // Mission / variable counts may refine once nested campaigns finish loading.
    modals.handleUpdateResetCampaignModal({
      rootMissionCount: rootTasks.length,
      nestedMissionCount: nestedTasks.length,
      variableValueCount,
    });

    const impact = summarizeResetImpact({ rootTasks, nestedTasks, docsById });
    modals.handleUpdateResetCampaignModal({ loading: false, ...impact, variableValueCount });
  })();
}
